import React, { useContext, useState, useEffect } from "react"
import StaffSidebar from "../../../components/sidebars/StaffSidebar"
import StaffProfileBox from "../../Staff/components/StaffProfileBox"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@apollo/client"
import PageHeader from "../../../components/PageHeader"
import { SplitButton } from "primereact/splitbutton"
import { LoaderMedium } from "../../../components/Loaders"
import {
  GROUP_SUPERVISION_SESSIONS,
  GET_STAFF_ID_BY_MEMBERSHIP_ID,
  GET_STAFF_PROFILE_BOX,
} from "../queries"
import { UserContext } from "../../../context/userContext"
import GenericTable from "../../../components/DataTable/GenericTable"
import { useFilters } from "../../../components/Filters/useFilters"
import FilterHeader from "../../../components/DataTable/FilterHeader"
import { Calendar } from "primereact/calendar"
import parseJsonData from "../../../helpers/parseJsonData"
import { parseDate } from "../../../helpers/dateNormalization"
import { Button } from "primereact/button"
import useLabels from "../../../hooks/useLabels"
import { navigateWithOrigin } from "../../../helpers/navigation"

const formatDate = (value) => {
  if (!value) return "Invalid Date"
  const date = new Date(value)
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export default function StaffGroupSession() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [dateRange, setDateRange] = useState(null)
  const staff_membership_id = userCtx.current_staff_membership_id
  const [error, setError] = useState(false)
  const [userIsSupervisor, setUserIsSupervisor] = useState(false)
  const [loadingLabels, labels] = useLabels(["group_supervision_session"])

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(GET_STAFF_ID_BY_MEMBERSHIP_ID, {
    variables: { staff_membership_id },
    fetchPolicy: "no-cache",
    skip: !staff_membership_id,
    onError: () => setError(true),
  })

  const staff_id_role_id = staffData?.staffDataByStaffMembershipID?.rol_id

  const { loading: userLoading, refetch: refetchUser } = useQuery(
    GET_STAFF_PROFILE_BOX,
    {
      variables: { organization_id: null, staff_id: null }, // Initially empty, refetch will update this
      skip: true, // Skip initial fetch, we will trigger refetch with variables later
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (!data?.getStaffProfileBox || data.getStaffProfileBox.length === 0) {
          return
        }
        if (
          userCtx.session.user.staff_id ===
          data.getStaffProfileBox.staff_primary_supervisor
        )
          setUserIsSupervisor(true)
      },
      onError: () => setError(true),
    }
  )

  const { loading, data } = useQuery(GROUP_SUPERVISION_SESSIONS, {
    variables: {
      idTemplate: "group_supervision_session",
      organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
      staff_id: staffData?.staffDataByStaffMembershipID?.staff_id,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.groupSupervisionSessions === null) {
        setSessions([])
        return setLoadingPage(false)
      }
      const formattedData = parseJsonData(data.groupSupervisionSessions.data)
      setSessions(formattedData)
      setLoadingPage(false)
    },
  })

  useEffect(() => {
    // Save the current relative path to local storage
    localStorage.setItem("groupSessionListPage", window.location.pathname)
  }, [])

  useEffect(() => {
    if (!staffData?.staffDataByStaffMembershipID) return

    const { organization_id, staff_id } = staffData.staffDataByStaffMembershipID

    if (organization_id && staff_id) {
      refetchUser({ organization_id, staff_id })
    }
  }, [refetchUser, staffData?.staffDataByStaffMembershipID])

  const formattedSessions = sessions.map((row) => {
    const locations = Array.isArray(row.session__location_ids)
      ? row.session__location_ids.map((loc) => loc.label).join(", ")
      : ""
    const programs = Array.isArray(row.group_supervision__program_identity_ids)
      ? row.group_supervision__program_identity_ids
          .map((prog) => prog.label)
          .join(", ")
      : ""
    const isStaffIdPresent =
      row.staff_notes_multiblock?.some(
        (item) =>
          item.staff_note__staff_id_selector?.key ===
          userCtx.session.user.staff_id
      ) ?? false //if staff_notes_multiblock is empty, then it will return false

    const facilitatingStaff = row.staff_full_name
    const length = `${row.session_length} minutes`
    const topics = Array.isArray(row.group_supervision_topic)
      ? row.group_supervision_topic.join(", ")
      : ""
    const sessionNotes = (
      <div dangerouslySetInnerHTML={{ __html: row.session_notes }} />
    )

    return {
      ...row,
      date: parseDate(row.session_date),
      locations,
      programs,
      isStaffIdPresent,
      facilitating_staff: facilitatingStaff,
      length,
      topics,
      session_notes: sessionNotes,
      // participating_staff: participatingStaff,
      edit:
        !isStaffIdPresent && userCtx.session.user.staff_role < 5 ? (
          <SplitButton
            label="View"
            onClick={() => {
              userCtx.changeParticipant(undefined)
              navigateWithOrigin(
                navigate,
                `/form/view/group_supervision_session/${row.idStamp}`
              )
            }}
            model={[
              {
                label: "Edit",
                command: () =>
                  navigateWithOrigin(
                    navigate,
                    `/form/group_supervision_session/1/${row.idStamp}`
                  ),
              },
            ]}
          />
        ) : (
          <Button
            label="View"
            onClick={() => {
              userCtx.changeParticipant(undefined)
              navigateWithOrigin(
                navigate,
                `/form/view/group_supervision_session/${row.idStamp}`
              )
            }}
          />
        ),
    }
  })

  if (loadingPage || loading || loadingLabels) return <LoaderMedium />

  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`
  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "locations", header: "Location", sortable: true },
    {
      field: "facilitating_staff",
      header: "Facilitating Staff",
      sortable: true,
    },
    /*{
      field: "participating_staff",
      header: "Participating Staff",
      sortable: true,
    },*/
    { field: "programs", header: "Programs", sortable: true },
    { field: "topics", header: "Topics", sortable: true },
    {
      field: "session_notes",
      header: "Session note",
      sortable: true,
    },
    { field: "edit", header: "Action" },
  ]

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={3}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Topics",
      value: filters.topics?.value || "",
      options: [
        ...new Set(
          sessions.flatMap((session) => session.group_supervision_topic)
        ),
      ].map((topic) => ({ label: topic, value: topic })),
      onChange: (e) => onFilterChange("topics", e.value),
      onClear: () => onFilterChange("topics", null),
      placeholder: "Select Topics",
    },
    {
      label: "Programs",
      value: filters.programs?.value || "",
      options: [
        ...new Set(
          sessions.flatMap((session) =>
            Array.isArray(session.group_supervision__program_identity_ids)
              ? session.group_supervision__program_identity_ids.map(
                  (prog) => prog.label
                )
              : []
          )
        ),
      ].map((program) => ({ label: program, value: program })),
      onChange: (e) => onFilterChange("programs", e.value),
      onClear: () => onFilterChange("programs", null),
      placeholder: "Select Programs",
    },
    {
      label: "Locations",
      value: filters.locations?.value || "",
      options: [
        ...new Set(
          sessions.flatMap((session) =>
            Array.isArray(session.session__location_ids)
              ? session.session__location_ids.map((loc) => loc.label)
              : []
          )
        ),
      ].map((location) => ({ label: location, value: location })),
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      placeholder: "Select Locations",
    },
    {
      label: "Facilitating Staff",
      value: filters.submittedBy?.value || "",
      options: [
        ...new Set(sessions.map((session) => session.staff_full_name)),
      ].map((submitter) => ({ label: submitter, value: submitter })),
      onChange: (e) => onFilterChange("submittedBy", e.value),
      onClear: () => onFilterChange("submittedBy", null),
      placeholder: "Select staff",
    },
  ]

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <StaffSidebar />
      </div>
      <div className="main-content">
        <StaffProfileBox />
        {userCtx.session.user.staff_role >= 0 &&
        userCtx.session.user.staff_role <= 4 &&
        staff_id_role_id !== "-1" ? (
          <PageHeader
            title={labels?.group_supervision_session}
            actions={[
              {
                to: `/form/group_supervision_session/1`,
                label: "Start a New " + labels?.group_supervision_session,
              },
            ]}
          />
        ) : (
          <PageHeader title={labels?.group_supervision_session} />
        )}

        <GenericTable
          data={formattedSessions}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={handleClearAllFilters}
            />
          }
          paginator
          rows={50}
          sortField="date"
        />
      </div>
    </div>
  )
}
